/**
 * PlanMatrix - Página de matrizes de canais e provedores por plano
 * Gerencia plan_channel_types e plan_providers com design premium de cards
 */

import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { 
  RefreshCw, 
  MessageSquare, 
  Plug, 
  Check, 
  X, 
  Plus,
  DollarSign
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { showToast } from '@/lib/toast';
import { applyCurrencyMask, parseCurrency } from '@/lib/input-masks';
import { cn } from '@/lib/utils';

// ============= Interfaces =============
interface Plan {
  id: string;
  name: string;
}

interface ChannelType {
  id: string;
  slug: string;
  display_name: string;
}

interface Provider {
  id: string;
  slug: string;
  display_name: string | null;
  name: string;
  channel_type: string; // Tipo de canal do provider para validação de padrão único
}

interface PlanChannelType {
  plan_id: string;
  channel_type_id: string;
  is_available: boolean;
  can_addon: boolean;
  addon_price_monthly: number | null;
  addon_setup_fee: number | null;
}

interface PlanProvider {
  plan_id: string;
  provider_id: string;
  is_available: boolean;
  is_default: boolean;
}

// ============= Componente PlanConfigCard para Canais =============
interface ChannelPlanCardProps {
  plan: Plan;
  config: PlanChannelType | undefined;
  channelId: string;
  onUpdate: (planId: string, channelId: string, field: keyof PlanChannelType, value: boolean | number | null) => void;
  saving: boolean;
}

const ChannelPlanCard = ({ plan, config, channelId, onUpdate, saving }: ChannelPlanCardProps) => {
  const isAvailable = config?.is_available ?? false;
  const canAddon = config?.can_addon ?? false;

  // Determina o estado visual do card
  const getCardState = () => {
    if (!isAvailable) return 'unavailable';
    if (canAddon) return 'addon';
    return 'included';
  };

  const state = getCardState();

  return (
    <div
      className={cn(
        'rounded-xl p-4 border-2 transition-all duration-200',
        state === 'unavailable' && 'border-dashed border-muted bg-muted/20',
        state === 'included' && 'border-green-500/50 bg-green-500/5',
        state === 'addon' && 'border-primary/50 bg-primary/5'
      )}
    >
      {/* Header do card com nome do plano e switch principal */}
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-sm">{plan.name}</span>
        <Switch
          checked={isAvailable}
          onCheckedChange={(checked) => onUpdate(plan.id, channelId, 'is_available', checked)}
          disabled={saving}
        />
      </div>

      {/* Badge de status */}
      <div className="mb-3">
        {state === 'unavailable' && (
          <Badge variant="secondary" className="bg-muted text-muted-foreground gap-1">
            <X className="h-3 w-3" />
            Indisponível
          </Badge>
        )}
        {state === 'included' && (
          <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800 gap-1">
            <Check className="h-3 w-3" />
            Incluso
          </Badge>
        )}
        {state === 'addon' && (
          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 gap-1">
            <Plus className="h-3 w-3" />
            Add-on
          </Badge>
        )}
      </div>

      {/* Conteúdo condicional quando disponível */}
      {isAvailable && (
        <div className="space-y-3 pt-3 border-t border-border/50">
          {/* Toggle Add-on */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Contratar como Add-on</span>
            <Switch
              checked={canAddon}
              onCheckedChange={(checked) => onUpdate(plan.id, channelId, 'can_addon', checked)}
              disabled={saving}
              className="scale-90"
            />
          </div>

          {/* Preços (apenas se add-on habilitado) */}
          {canAddon && (
            <div className="space-y-2 animate-fade-in">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase font-medium mb-1 block">
                    Mensal
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      className="h-8 text-xs pl-6"
                      placeholder="0,00"
                      value={config?.addon_price_monthly ? applyCurrencyMask(config.addon_price_monthly) : ''}
                      onChange={(e) => {
                        const val = parseCurrency(e.target.value);
                        onUpdate(plan.id, channelId, 'addon_price_monthly', val || null);
                      }}
                      disabled={saving}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase font-medium mb-1 block">
                    Setup
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      className="h-8 text-xs pl-6"
                      placeholder="0,00"
                      value={config?.addon_setup_fee ? applyCurrencyMask(config.addon_setup_fee) : ''}
                      onChange={(e) => {
                        const val = parseCurrency(e.target.value);
                        onUpdate(plan.id, channelId, 'addon_setup_fee', val || null);
                      }}
                      disabled={saving}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============= Componente PlanConfigCard para Provedores =============
interface ProviderPlanCardProps {
  plan: Plan;
  config: PlanProvider | undefined;
  providerId: string;
  onUpdate: (planId: string, providerId: string, field: 'is_available' | 'is_default', value: boolean) => void;
  saving: boolean;
}

const ProviderPlanCard = ({ plan, config, providerId, onUpdate, saving }: ProviderPlanCardProps) => {
  const isAvailable = config?.is_available ?? false;
  const isDefault = config?.is_default ?? false;

  return (
    <div
      className={cn(
        'rounded-xl p-4 border-2 transition-all duration-200',
        !isAvailable && 'border-dashed border-muted bg-muted/20',
        isAvailable && !isDefault && 'border-green-500/50 bg-green-500/5',
        isAvailable && isDefault && 'border-amber-500/50 bg-amber-500/5'
      )}
    >
      {/* Header do card */}
      <div className="flex items-center justify-between mb-3">
        <span className="font-semibold text-sm">{plan.name}</span>
        <Switch
          checked={isAvailable}
          onCheckedChange={(checked) => onUpdate(plan.id, providerId, 'is_available', checked)}
          disabled={saving}
        />
      </div>

      {/* Badge de status */}
      <div className="mb-3">
        {!isAvailable && (
          <Badge variant="secondary" className="bg-muted text-muted-foreground gap-1">
            <X className="h-3 w-3" />
            Indisponível
          </Badge>
        )}
        {isAvailable && !isDefault && (
          <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800 gap-1">
            <Check className="h-3 w-3" />
            Disponível
          </Badge>
        )}
        {isAvailable && isDefault && (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800 gap-1">
            <Check className="h-3 w-3" />
            Padrão
          </Badge>
        )}
      </div>

      {/* Toggle Padrão (apenas se disponível) */}
      {isAvailable && (
        <div className="pt-3 border-t border-border/50">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Definir como padrão</span>
            <Switch
              checked={isDefault}
              onCheckedChange={(checked) => onUpdate(plan.id, providerId, 'is_default', checked)}
              disabled={saving}
              className="scale-90"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ============= Componente Principal =============
const AdminPlanMatrix = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [channelTypes, setChannelTypes] = useState<ChannelType[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [planChannelTypes, setPlanChannelTypes] = useState<PlanChannelType[]>([]);
  const [planProviders, setPlanProviders] = useState<PlanProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Busca todos os dados necessários
  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, channelsRes, providersRes, pctRes, ppRes] = await Promise.all([
        supabase.from('subscription_plans').select('id, name').order('sort_order'),
        supabase.from('channel_types').select('id, slug, display_name').order('sort_order'),
        supabase.from('api_providers').select('id, slug, display_name, name, channel_type').order('name'),
        supabase.from('plan_channel_types').select('*'),
        supabase.from('plan_providers').select('*'),
      ]);

      if (plansRes.error) throw plansRes.error;
      if (channelsRes.error) throw channelsRes.error;
      if (providersRes.error) throw providersRes.error;
      if (pctRes.error) throw pctRes.error;
      if (ppRes.error) throw ppRes.error;

      setPlans(plansRes.data || []);
      setChannelTypes(channelsRes.data || []);
      setProviders(providersRes.data || []);
      setPlanChannelTypes(pctRes.data || []);
      setPlanProviders(ppRes.data || []);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      showToast.error('Não foi possível carregar os dados');
    } finally {
      setLoading(false);
    }
  };

  // Busca configuração de canal para um plano
  const getChannelConfig = (planId: string, channelTypeId: string): PlanChannelType | undefined => {
    return planChannelTypes.find(
      (pct) => pct.plan_id === planId && pct.channel_type_id === channelTypeId
    );
  };

  // Busca configuração de provider para um plano
  const getProviderConfig = (planId: string, providerId: string): PlanProvider | undefined => {
    return planProviders.find(
      (pp) => pp.plan_id === planId && pp.provider_id === providerId
    );
  };

  // Atualiza ou cria configuração de canal
  const updateChannelConfig = async (
    planId: string,
    channelTypeId: string,
    field: keyof PlanChannelType,
    value: boolean | number | null
  ) => {
    setSaving(true);
    try {
      const existing = getChannelConfig(planId, channelTypeId);

      if (existing) {
        const { error } = await supabase
          .from('plan_channel_types')
          .update({ [field]: value } as Database['public']['Tables']['plan_channel_types']['Update'])
          .eq('plan_id', planId)
          .eq('channel_type_id', channelTypeId);

        if (error) throw error;
      } else {
        const newConfig: PlanChannelType = {
          plan_id: planId,
          channel_type_id: channelTypeId,
          is_available: field === 'is_available' ? (value as boolean) : false,
          can_addon: field === 'can_addon' ? (value as boolean) : false,
          addon_price_monthly: field === 'addon_price_monthly' ? (value as number) : null,
          addon_setup_fee: field === 'addon_setup_fee' ? (value as number) : null,
        };

        const { error } = await supabase.from('plan_channel_types').insert(newConfig);
        if (error) throw error;
      }

      // Atualiza estado local
      setPlanChannelTypes((prev) => {
        const idx = prev.findIndex(
          (p) => p.plan_id === planId && p.channel_type_id === channelTypeId
        );
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], [field]: value };
          return updated;
        }
        return [
          ...prev,
          {
            plan_id: planId,
            channel_type_id: channelTypeId,
            is_available: field === 'is_available' ? (value as boolean) : false,
            can_addon: field === 'can_addon' ? (value as boolean) : false,
            addon_price_monthly: field === 'addon_price_monthly' ? (value as number) : null,
            addon_setup_fee: field === 'addon_setup_fee' ? (value as number) : null,
          },
        ];
      });

      showToast.success('Configuração atualizada');
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      showToast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  // Atualiza ou cria configuração de provider
  // Inclui validação para impedir 2 providers padrão do mesmo channel_type
  const updateProviderConfig = async (
    planId: string,
    providerId: string,
    field: 'is_available' | 'is_default',
    value: boolean
  ) => {
    setSaving(true);
    try {
      // Validação: ao marcar is_default=true, verificar se já existe outro padrão do mesmo channel_type
      if (field === 'is_default' && value === true) {
        const currentProvider = providers.find(p => p.id === providerId);
        if (currentProvider) {
          // Buscar providers do mesmo channel_type que já são padrão para este plano
          const existingDefault = planProviders.find(pp => 
            pp.plan_id === planId && 
            pp.is_default === true && 
            pp.provider_id !== providerId
          );
          
          if (existingDefault) {
            // Verificar se o provider existente é do mesmo channel_type
            const existingProvider = providers.find(p => p.id === existingDefault.provider_id);
            if (existingProvider && existingProvider.channel_type === currentProvider.channel_type) {
              showToast.error(
                `Já existe um provider padrão para o canal "${currentProvider.channel_type}". Desmarque "${existingProvider.display_name || existingProvider.name}" primeiro.`
              );
              setSaving(false);
              return;
            }
          }
        }
      }

      const existing = getProviderConfig(planId, providerId);

      if (existing) {
        const { error } = await supabase
          .from('plan_providers')
          .update({ [field]: value } as Database['public']['Tables']['plan_providers']['Update'])
          .eq('plan_id', planId)
          .eq('provider_id', providerId);

        if (error) throw error;
      } else {
        const newConfig: PlanProvider = {
          plan_id: planId,
          provider_id: providerId,
          is_available: field === 'is_available' ? value : false,
          is_default: field === 'is_default' ? value : false,
        };

        const { error } = await supabase.from('plan_providers').insert(newConfig);
        if (error) throw error;
      }

      // Atualiza estado local
      setPlanProviders((prev) => {
        const idx = prev.findIndex(
          (p) => p.plan_id === planId && p.provider_id === providerId
        );
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = { ...updated[idx], [field]: value };
          return updated;
        }
        return [
          ...prev,
          {
            plan_id: planId,
            provider_id: providerId,
            is_available: field === 'is_available' ? value : false,
            is_default: field === 'is_default' ? value : false,
          },
        ];
      });

      showToast.success('Configuração atualizada');
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      showToast.error('Erro ao salvar configuração');
    } finally {
      setSaving(false);
    }
  };

  // Contagem de canais/providers configurados
  const getChannelConfiguredCount = (channelId: string) => {
    return planChannelTypes.filter(p => p.channel_type_id === channelId && p.is_available).length;
  };

  const getProviderConfiguredCount = (providerId: string) => {
    return planProviders.filter(p => p.provider_id === providerId && p.is_available).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Matriz de Planos</h1>
          <p className="text-muted-foreground">Configure canais e provedores disponíveis por plano</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      <Tabs defaultValue="channels" className="space-y-6">
        {/* TabsList com badges */}
        <div className="flex items-center justify-between">
          <TabsList className="bg-muted/50 p-1">
            <TabsTrigger value="channels" className="data-[state=active]:bg-background data-[state=active]:shadow-sm gap-2">
              <MessageSquare className="h-4 w-4" />
              Canais
            </TabsTrigger>
            <TabsTrigger value="providers" className="data-[state=active]:bg-background data-[state=active]:shadow-sm gap-2">
              <Plug className="h-4 w-4" />
              Provedores
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {channelTypes.length} canais
            </Badge>
            <Badge variant="outline" className="text-xs">
              {providers.length} provedores
            </Badge>
            <Badge variant="outline" className="text-xs">
              {plans.length} planos
            </Badge>
          </div>
        </div>

        {/* Tab: Canais x Planos */}
        <TabsContent value="channels" className="space-y-4">
          {channelTypes.map((channel) => (
            <div
              key={channel.id}
              className="bg-card border rounded-xl p-5 hover:shadow-md transition-shadow"
            >
              {/* Header do card de canal */}
              <div className="flex items-center gap-4 mb-5">
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{channel.display_name}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {getChannelConfiguredCount(channel.id)}/{plans.length} planos
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{channel.slug}</p>
                </div>
              </div>

              {/* Grid de planos */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {plans.map((plan) => (
                  <ChannelPlanCard
                    key={plan.id}
                    plan={plan}
                    config={getChannelConfig(plan.id, channel.id)}
                    channelId={channel.id}
                    onUpdate={updateChannelConfig}
                    saving={saving}
                  />
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Tab: Provedores x Planos */}
        <TabsContent value="providers" className="space-y-4">
          {providers.map((provider) => (
            <div
              key={provider.id}
              className="bg-card border rounded-xl p-5 hover:shadow-md transition-shadow"
            >
              {/* Header do card de provider */}
              <div className="flex items-center gap-4 mb-5">
                <div className="h-12 w-12 rounded-xl bg-accent/50 flex items-center justify-center">
                  <Plug className="h-6 w-6 text-accent-foreground" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{provider.display_name || provider.name}</h3>
                    <Badge variant="secondary" className="text-xs">
                      {getProviderConfiguredCount(provider.id)}/{plans.length} planos
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{provider.slug}</p>
                </div>
              </div>

              {/* Grid de planos */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {plans.map((plan) => (
                  <ProviderPlanCard
                    key={plan.id}
                    plan={plan}
                    config={getProviderConfig(plan.id, provider.id)}
                    providerId={provider.id}
                    onUpdate={updateProviderConfig}
                    saving={saving}
                  />
                ))}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPlanMatrix;
